import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, X, ExternalLink, RefreshCw, Loader2 } from 'lucide-react'
import { formatPrice } from '../../utils/formatPrice'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Input } from '../ui/input'
import { ImageGalleryEditor } from './ImageGalleryEditor'
import { InlineEditField } from './InlineEditField'
import { InlineEditBrand } from './InlineEditBrand'
import { InlineEditCategory } from './InlineEditCategory'
import { InlineEditTags } from './InlineEditTags'
import { useCategoryNames } from './CategorySelector'
import { urlScraper } from '../../lib/urlScraper'
import { supabase } from '../../lib/supabase'
import { classifyItem } from '../../lib/classificationEngine'
import type { ImageData } from '../../types/images'

interface ProductFormData {
  id?: string | null
  title: string
  brand: string
  brand_id?: string | null
  description: string
  image_url?: string
  cached_image_url?: string
  images?: ImageData[]
  category?: string
  category_name?: string
  subcategory?: string
  subcategory_name?: string
  tags?: string[]
  tag_names?: string[]
  impact?: string
  source_url?: string
  price_amount?: number | null
  price_currency?: string | null
  comment?: string
  user_id?: string
  created_at?: string
  saves_count?: number
  is_saved?: boolean
  _classification?: any
  _selectedCategoryId?: string | null
  _selectedSubcategoryId?: string | null
}

interface StreamlinedProductFormProps {
  initialData: ProductFormData
  onSave: (data: ProductFormData) => Promise<void>
  onCancel: () => void
  mode: 'add' | 'edit'
  isAdmin?: boolean
  isEmbedded?: boolean
  currentUserId?: string
  canDelete?: boolean
  onDelete?: () => Promise<void>
  initialError?: string | null // e.g. a scrape failure/blocked-site message to show above the form
}

export function StreamlinedProductForm({
  initialData,
  onSave,
  onCancel,
  mode,
  isAdmin = false,
  isEmbedded = false,
  currentUserId,
  canDelete = false,
  onDelete,
  initialError = null
}: StreamlinedProductFormProps) {
  // Form state
  const [formData, setFormData] = useState<ProductFormData>(initialData)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    initialData._selectedCategoryId || null
  )
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(
    initialData._selectedSubcategoryId || null
  )

  // Classification state - preserve from scraping for save
  const [classificationResult, setClassificationResult] = useState<any>(
    initialData._classification || null
  )

  // Get category names for display mode
  const { categoryName, subcategoryName } = useCategoryNames(selectedCategoryId, selectedSubcategoryId)

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({})
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(initialError ?? null)
  const [isRescraping, setIsRescraping] = useState(false)
  const [showAddImageModal, setShowAddImageModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [newImageUrl, setNewImageUrl] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  const commentRef = useRef<HTMLTextAreaElement>(null)

  // Autofocus comment field in add mode
  useEffect(() => {
    if (mode === 'add' && commentRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        commentRef.current?.focus()
      }, 100)
    }
  }, [mode])

  // Apply classification data from scraping (look up IDs from names if needed)
  useEffect(() => {
    const applyClassification = async () => {
      if (!classificationResult) return

      // If we already have category ID set, skip
      if (selectedCategoryId) return

      // First try IDs (from rule-based classification)
      if (classificationResult.category_id) {
        setSelectedCategoryId(classificationResult.category_id)
        if (classificationResult.subcategory_id) {
          setSelectedSubcategoryId(classificationResult.subcategory_id)
        }
        return
      }

      // If no IDs but we have names (from LLM classification), look up IDs
      if (classificationResult.category_name) {
        try {
          const { data: categories } = await supabase
            .from('categories')
            .select('id, name')

          if (categories) {
            const category = categories.find(
              c => c.name.toLowerCase() === classificationResult.category_name.toLowerCase()
            )
            if (category) {
              setSelectedCategoryId(category.id)

              // Update classification result with the found ID
              setClassificationResult((prev: any) => ({
                ...prev,
                category_id: category.id
              }))

              // Look up subcategory if we have a name
              if (classificationResult.subcategory_name) {
                const { data: subcategories } = await supabase
                  .from('subcategories')
                  .select('id, name, category_id')
                  .eq('category_id', category.id)

                if (subcategories) {
                  const subcategory = subcategories.find(
                    s => s.name.toLowerCase() === classificationResult.subcategory_name.toLowerCase()
                  )
                  if (subcategory) {
                    setSelectedSubcategoryId(subcategory.id)
                    setClassificationResult((prev: any) => ({
                      ...prev,
                      subcategory_id: subcategory.id
                    }))
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error looking up category IDs:', error)
        }
      }
    }

    applyClassification()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationResult?.category_name, classificationResult?.category_id])

  // Track dirty state
  useEffect(() => {
    setIsDirty(true)
  }, [formData, selectedCategoryId, selectedSubcategoryId])

  // Handle beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Update form field
  const handleFieldChange = (field: keyof ProductFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }))
    }
  }

  // Handle image changes
  const handleImagesChange = (images: ImageData[]) => {
    handleFieldChange('images', images)
    // Update main image URL to the first selected/highlighted image
    const highlightedImage = images.find(img => img.highlighted && img.selected)
    const firstSelectedImage = images.find(img => img.selected)
    const mainImage = highlightedImage || firstSelectedImage
    if (mainImage) {
      handleFieldChange('image_url', mainImage.cached_url || mainImage.url)
    }
  }

  // Handle add image from URL
  const handleAddImageUrl = () => {
    if (newImageUrl && newImageUrl.trim()) {
      const newImage: ImageData = {
        url: newImageUrl.trim(),
        src: newImageUrl.trim(),
        alt: 'Product image',
        selected: true,
        order: formData.images?.length || 0
      }

      const updatedImages = [...(formData.images || []), newImage]
      handleImagesChange(updatedImages)

      // If this is the first image, set it as the main image
      if (!formData.image_url || updatedImages.length === 1) {
        handleFieldChange('image_url', newImageUrl.trim())
      }

      setNewImageUrl('')
      setShowAddImageModal(false)
    }
  }

  // Handle re-scrape (admin feature)
  const handleRescrape = async () => {
    if (!formData.source_url) return

    try {
      setIsRescraping(true)
      const productData = await urlScraper.scrapeProduct(formData.source_url)

      // Update form with new scraped data
      setFormData(prev => ({
        ...prev,
        title: productData.title || prev.title,
        brand: productData.brand || prev.brand,
        description: productData.description || prev.description,
        image_url: productData.cached_image_url || productData.image_url || prev.image_url,
        cached_image_url: productData.cached_image_url || prev.cached_image_url,
        images: productData.images?.map((img: any, index: number) => ({
          ...img,
          selected: index < 3,
          highlighted: img.highlighted !== undefined ? img.highlighted : index === 0
        })) || prev.images
      }))

      // Auto-classify the re-scraped data
      try {
        const classification = await classifyItem(
          productData.title || formData.title || '',
          productData.description || formData.description || '',
          formData.source_url,
          { brand: productData.brand || formData.brand || '' }
        )

        if (classification.category_id) {
          setSelectedCategoryId(classification.category_id)
        }
        if (classification.subcategory_id) {
          setSelectedSubcategoryId(classification.subcategory_id)
        }

        // Update tags from classification
        const newTags = classification.tags?.map((t: any) => t.tag_name) || []
        if (newTags.length > 0) {
          setFormData(prev => {
            const existingTags = prev.tags || []
            const mergedTags = Array.from(new Set([...existingTags, ...newTags]))
            return { ...prev, tags: mergedTags }
          })
        }

        // Store the classification result
        setClassificationResult(classification)
      } catch (classifyError) {
        console.error('Classification failed during re-scraping:', classifyError)
        // Classification failed, continue without it
      }
    } catch (err: any) {
      console.error('Re-scrape failed:', err)
      setError('Failed to re-scrape product data')
    } finally {
      setIsRescraping(false)
    }
  }

  // Validate and save
  const handleSave = async () => {
    // Validate required fields
    const errors: { [key: string]: boolean } = {}

    if (!formData.title || formData.title.trim() === '') {
      errors.title = true
    }
    if (!formData.brand || formData.brand.trim() === '') {
      errors.brand = true
    }
    if (!selectedCategoryId) {
      errors.category = true
    }
    if (!formData.description || formData.description.trim() === '') {
      errors.description = true
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setError('Please fill in all required fields')
      return
    }

    try {
      setIsSaving(true)
      setError(null)

      // Filter images to only include selected ones
      const selectedImages = formData.images?.filter(img => img.selected) || []

      // Build classification with current category/subcategory IDs and form tags
      // Convert form tags (string array) to classification format (objects with tag_name)
      const currentTags = (formData.tags || formData.tag_names || []).map((name: string) => ({ tag_name: name }))

      const classificationToSave = classificationResult ? {
        ...classificationResult,
        category_id: selectedCategoryId,
        subcategory_id: selectedSubcategoryId,
        tags: currentTags // Use current form tags, not original classification tags
      } : selectedCategoryId ? {
        category_id: selectedCategoryId,
        subcategory_id: selectedSubcategoryId,
        tags: currentTags
      } : null

      const saveData = {
        ...formData,
        title: formData.title.trim(),
        brand: formData.brand.trim(),
        description: formData.description.trim(),
        images: selectedImages,
        tags: formData.tags || formData.tag_names || [],
        _selectedCategoryId: selectedCategoryId,
        _selectedSubcategoryId: selectedSubcategoryId,
        _classification: classificationToSave,
        _skipAutoClassify: !!classificationToSave
      }

      await onSave(saveData)
      setIsDirty(false)
    } catch (err: any) {
      console.error('Save failed:', err)
      setError(err.message || 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle cancel with dirty check
  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardModal(true)
      return
    }
    onCancel()
  }

  // Confirm discard and close
  const handleConfirmDiscard = () => {
    setShowDiscardModal(false)
    setIsDirty(false)
    onCancel()
  }

  // Handle delete
  const handleDelete = () => {
    if (!onDelete) return
    setShowDeleteModal(true)
  }

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!onDelete) return
    setShowDeleteModal(false)

    try {
      await onDelete()
    } catch (err: any) {
      console.error('Delete failed:', err)
      setError(err.message || 'Failed to delete item')
    }
  }

  const isAddMode = mode === 'add'

  return (
    <div className={isEmbedded ? "fixed inset-0 flex bg-[#F2F0E5]" : "fixed inset-0 top-[73px] flex bg-[#F2F0E5]"}>
      {/* Left Side Panel (desktop only) */}
      <div className="hidden lg:block w-[50px] bg-[#F2F0E5] flex-shrink-0 relative z-0" />

      {/* Main Content */}
      <div className="flex-1 bg-white relative z-10 grid grid-rows-[auto_1fr] lg:[box-shadow:0_0_50px_rgba(0,0,0,0.15)]">
        {/* Header Bar */}
        <div className="bg-white border-b px-6 flex items-center justify-between z-20" style={{ height: '64px', paddingTop: '4px' }}>
          {/* Back/Close Button */}
          <button
            onClick={handleCancel}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={isEmbedded ? "Close" : "Go back"}
          >
            {isEmbedded ? <X className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
          </button>

          {/* Cancel Button */}
          <button
            onClick={handleCancel}
            className="text-sm font-medium text-gray-500 hover:text-black transition-colors px-3 py-1.5"
            style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
          >
            Cancel
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
              {/* Left Column - Image Gallery */}
              <div className="lg:sticky lg:top-6">
                <ImageGalleryEditor
                  images={formData.images || []}
                  onImagesChange={handleImagesChange}
                  cachedImageUrl={formData.cached_image_url}
                  imageUrl={formData.image_url}
                  sourceUrl={formData.source_url}
                  title={formData.title}
                  isEditable={true}
                  onAddImage={() => setShowAddImageModal(true)}
                  maxRecommended={3}
                />
              </div>

              {/* Right Column - Form */}
              <div className="space-y-10">
                {/* Primary Section - Why are you sharing? */}
                <section className="space-y-4">
                  <h2
                    className="text-xl lg:text-2xl font-medium leading-tight"
                    style={{ fontFamily: 'Canela, Georgia, serif' }}
                  >
                    Why are you sharing this with the community?
                  </h2>

                  <div className="relative">
                    <textarea
                      ref={commentRef}
                      value={formData.comment || ''}
                      onChange={(e) => handleFieldChange('comment', e.target.value)}
                      placeholder="[Optional] Share why this product is special to you..."
                      rows={3}
                      className="w-full bg-white border-2 border-[#57241F]/10 rounded-xl p-4 text-base focus:ring-4 focus:ring-[#57241F]/5 focus:border-[#57241F] transition-all outline-none placeholder:text-gray-300 shadow-sm leading-relaxed resize-none"
                      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Primary CTA Button */}
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full bg-[#57241F] text-white py-5 rounded-2xl font-bold tracking-widest uppercase text-sm shadow-xl shadow-[#57241F]/20 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : isAddMode ? (
                      'Finish & Share'
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </section>

                {/* Pre-filled Details Section */}
                <section className="pt-10 border-t border-gray-100 space-y-8">
                  <div className="flex items-center justify-between">
                    <h3
                      className="text-xs font-bold text-gray-400 uppercase tracking-widest"
                      style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                    >
                      Pre-filled Details
                    </h3>
                    <span
                      className="text-xs text-gray-400 italic"
                      style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                    >
                      Click any field below to edit
                    </span>
                  </div>

                  {/* Product Name & Brand - 2 column grid */}
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                    {/* Product Name */}
                    <InlineEditField
                      label="Product Name"
                      value={formData.title || ''}
                      onChange={(value) => handleFieldChange('title', value)}
                      required
                      error={fieldErrors.title}
                    />

                    {/* Brand */}
                    <InlineEditBrand
                      label="Brand"
                      value={formData.brand || ''}
                      onChange={(brandName, brandId) => {
                        handleFieldChange('brand', brandName)
                        handleFieldChange('brand_id', brandId)
                      }}
                      required
                      error={fieldErrors.brand}
                    />
                  </div>

                  {/* Price (scraped, read-only) */}
                  {formatPrice(formData.price_amount, formData.price_currency) && (
                    <div>
                      <div className="text-xs font-bold text-gray-400 uppercase tracking-widest" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                        Price
                      </div>
                      <p className="text-lg text-gray-900 mt-1" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
                        {formatPrice(formData.price_amount, formData.price_currency)}
                      </p>
                    </div>
                  )}

                  {/* Category & Subcategory */}
                  <InlineEditCategory
                    selectedCategoryId={selectedCategoryId}
                    selectedSubcategoryId={selectedSubcategoryId}
                    onCategoryChange={setSelectedCategoryId}
                    onSubcategoryChange={setSelectedSubcategoryId}
                    categoryName={categoryName || undefined}
                    subcategoryName={subcategoryName || undefined}
                    categoryError={fieldErrors.category}
                  />

                  {/* Description */}
                  <InlineEditField
                    label="Description"
                    value={formData.description || ''}
                    onChange={(value) => handleFieldChange('description', value)}
                    type="textarea"
                    rows={4}
                    required
                    error={fieldErrors.description}
                  />

                  {/* Tags */}
                  <InlineEditTags
                    tags={formData.tags || formData.tag_names || []}
                    onChange={(tags) => handleFieldChange('tags', tags)}
                  />

                  {/* Source URL */}
                  <div>
                    <InlineEditField
                      label="Product Link"
                      value={formData.source_url || ''}
                      onChange={(value) => handleFieldChange('source_url', value.trim())}
                      placeholder="https://example.com/product"
                    />
                    {formData.source_url && (
                      <div className="mt-2 flex items-center justify-between">
                        <a
                          href={formData.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-blue-500 transition-colors inline-flex items-center gap-1"
                          style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                        >
                          Open link <ExternalLink className="h-3 w-3" />
                        </a>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRescrape}
                            disabled={isRescraping}
                            className="text-xs h-6"
                          >
                            {isRescraping ? (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Re-scraping...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1" /> Re-scrape
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Delete Button (at bottom, muted) */}
                  {canDelete && onDelete && (
                    <div className="pt-8 flex justify-center">
                      <button
                        onClick={handleDelete}
                        className="text-xs text-gray-300 font-bold tracking-widest uppercase hover:text-red-400 transition-colors"
                        style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                      >
                        Delete Item
                      </button>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side Panel (desktop only) */}
      <div className="hidden lg:block w-[50px] bg-[#F2F0E5] relative z-0" />

      {/* Add Image Modal */}
      <Dialog open={showAddImageModal} onOpenChange={setShowAddImageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Image URL</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddImageUrl()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddImageModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddImageUrl} disabled={!newImageUrl.trim()}>
              Add Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard Changes Confirmation Modal */}
      <Dialog open={showDiscardModal} onOpenChange={setShowDiscardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 text-sm">
              You have unsaved changes that will be lost if you leave this page.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiscardModal(false)}
            >
              Keep Editing
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDiscard}
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this item?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 text-sm">
              This action cannot be undone. The item will be permanently removed.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
            >
              Delete Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
